import type {DraftIssueProjectReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {searchProjectsQueryOptions} from '@github-ui/item-picker-project-tsq'
import {useQuery, useQueryClient} from '@github-ui/react-query'

export function useProjectsQuery({
  owner,
  repo,
  projects,
}: {
  owner?: string
  repo?: string
  projects?: DraftIssueProjectReference[]
}) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['copilot-immersive-projects', owner, repo, projects],
    queryFn: async () => {
      if (!owner || !repo || !projects || !projects.length) return []

      const desiredIds = new Set(projects.filter(project => Boolean(project.id)).map(project => project.id!))
      const desiredTitles = projects.map(project => project.title).filter((title): title is string => Boolean(title))
      const desiredTitlesLower = new Set(desiredTitles.map(title => title.toLowerCase()))

      // Check if all projects have IDs - if so, use ID-only matching for precision
      const allProjectsHaveIds = projects.length > 0 && projects.every(project => Boolean(project.id))

      const queryTerms = desiredTitles.length > 0 ? Array.from(new Set(desiredTitles)).join(',') : ''

      const {repositoryProjects, recentRepositoryProjects, ownerProjects, recentOwnerProjects} =
        await queryClient.fetchQuery(searchProjectsQueryOptions({owner, repo, query: queryTerms}))

      const uniqProjects = [
        ...[...repositoryProjects, ...recentRepositoryProjects, ...ownerProjects, ...recentOwnerProjects]
          .reduce((acc, item) => {
            acc.set(item.id, item)
            return acc
          }, new Map<string, ProjectPickerProject>())
          .values(),
      ]

      const filteredProjects = uniqProjects
        .filter(item => item.closed === false)
        .filter(item => item.hasReachedItemsLimit === false)
        // Despite querying by specific names, the query still returns several irrelevant projects
        // This adds an id and case-insensitive comparison on title, which should be what the user is asking for.
        .filter(item => {
          // If all projects have IDs, use ID-only matching for precision
          if (allProjectsHaveIds) {
            return item.id && desiredIds.has(item.id)
          }

          // Otherwise, use the existing mixed logic
          // Match by ID if this item has a desired ID
          if (item.id && desiredIds.has(item.id)) {
            return true
          }

          // Otherwise, match by title if this item's title is desired
          return desiredTitlesLower.has(item.title.toLowerCase())
        })

      // Remove duplicates - use different strategies based on matching approach
      if (allProjectsHaveIds) {
        // When using ID-only matching, no deduplication needed since IDs are unique
        return filteredProjects
      } else {
        // When using mixed matching, deduplicate by title and prioritize projects with desired IDs
        const seenTitles = new Set<string>()
        const sortedProjects = filteredProjects.sort((a, b) => {
          // Prioritize projects with desired IDs
          const aHasDesiredId = desiredIds.has(a.id)
          const bHasDesiredId = desiredIds.has(b.id)

          if (aHasDesiredId && !bHasDesiredId) return -1
          if (!aHasDesiredId && bHasDesiredId) return 1
          return 0
        })

        return sortedProjects.filter(item => {
          const lowerTitle = item.title.toLowerCase()
          if (seenTitles.has(lowerTitle)) {
            return false
          }
          seenTitles.add(lowerTitle)
          return true
        })
      }
    },
  })
}
